import { buildGroupingWork } from "../KarlGroupingPhase/groupingWork";
import { buildSourceRoster } from "../KarlSourcesPhase/sourceRoster";
import type { PlanBoard } from "../KarlSourcesPhase/sourceRoster";
import { buildBoardAsks } from "./buildBoardAsks";
import { buildHeightAsks } from "./buildHeightAsks";
import { buildRemovalAsks } from "../karlRoutes/buildRemovalAsks";
import { buildRouteAsks } from "../karlRoutes/buildRouteAsks";
import { buildRouteWalk } from "../karlRoutes/buildRouteWalk";
import { readRouteStatus } from "../karlRoutes/routeStatus";
import type { AddedCableSource } from "../cableEditGeometry";
import type {
  KarlGroupingItem,
  KarlGroupingParked,
  KarlPlanTags,
} from "../KarlGroupingPhase/groupingWork";
import type { GapTranslate } from "../findCableGaps";
import type { KarlStepsResult } from "../karlSteps";
import type { RemovalGroup } from "../cableEditGeometry";
import type { RemovedDevice } from "../RemovedDevices";
import type { KarlAsk } from "./types";
import type { MainBoardView } from "../mainBoard";
import type { KarlBoardFeeds, KarlFeeders, KarlHeights } from "../CablesChatPanel/types";

export interface BuildKarlAsksArgs {
  steps: KarlStepsResult;
  /** Identifiers printed on this plan. Defaults to the run's own list. */
  tags?: KarlPlanTags | null;
  added?: Record<string, AddedCableSource>;
  removed?: Record<string, unknown>;
  placed?: Record<string, unknown>;
  /** Source keys the estimator has already accepted in this session. */
  confirmed?: string[];
  /**
   * What he has settled, so a route he called wrong can grow its follow-up and
   * a route he confirmed never does.
   */
  settled?: Array<{ askId: string; outcome: string }>;
  /** Devices he has struck off, and the actions that struck them. */
  removedDevices?: RemovedDevice[];
  removalGroups?: RemovalGroup[];
  /** Zones he has drawn per board label, so the zone ask can say what is there. */
  zoneCountByLabel?: Record<string, number>;
  /** His board renames, by marker label. */
  sourceNames?: Record<string, string>;
  /** The app's main board, so the board step asks it first and the roster ranks it. */
  mainBoard?: MainBoardView | null;
  /** What each board feeds, so the board step asks it and names unfed categories. */
  feeds?: KarlBoardFeeds;
  /** Board, installation and tray heights; absent = no height is asked. */
  heights?: KarlHeights;
  /** Who feeds each board; absent = no feeder is asked. */
  feeders?: KarlFeeders;
  planBoards?: PlanBoard[];
  t: GapTranslate;
}

/**
 * A fact, not a question. A schedule the package never contained cannot be
 * fixed by any answer on this screen, so it is stated once beside the queue
 * instead of standing in it as a step that changes nothing.
 */
export interface KarlAskNote {
  id: string;
  text: string;
  /** Boards, ways or groups the fact is about. */
  subjects: string[];
  devices: number;
}

export interface KarlAsksResult {
  asks: KarlAsk[];
  notes: KarlAskNote[];
}

export type BuildKarlAsks = (args: BuildKarlAsksArgs) => KarlAsksResult;

const line = (
  t: GapTranslate,
  key: string,
  params?: Record<string, string | number>,
): string => t(`smartSurveyor.cablesBeta.${key}`, params);

/** The roster key an answered `source:<kind>:<key>` ask was about. */
const answeredSourceKeys = (
  settled: Array<{ askId: string }>,
): Set<string> => {
  const keys = new Set<string>();
  for (const { askId } of settled) {
    if (!askId.startsWith("source:")) continue;
    const kindEnd = askId.indexOf(":", "source:".length);
    if (kindEnd > 0) keys.add(askId.slice(kindEnd + 1));
  }
  return keys;
};

/**
 * A way the schematic lists that caught no devices. Still a fact and not an ask
 * — Karl does not walk a run nobody has made yet — but no longer a dead end:
 * the same ways are offered by `buildUnfilledWays` on the runs list's ＋, where
 * creating one is a click. Offering "draw the box" HERE still promises work the
 * answer cannot complete, because a box has to land on a circuit that exists.
 */
const emptyWayNote = (
  work: KarlGroupingItem[],
  t: GapTranslate,
): KarlAskNote[] => {
  const ways = work.filter((item) => item.kind === "empty_way");
  if (!ways.length) return [];
  return [
    {
      id: "note:empty_way",
      text: line(t, "noteEmptyWays", { count: ways.length }),
      subjects: ways.map((item) => item.label),
      devices: 0,
    },
  ];
};

const notesOf = (
  parked: KarlGroupingParked[],
  t: GapTranslate,
): KarlAskNote[] => {
  // One reason, one line, however many entries the run published under it.
  const merged = new Map<string, KarlGroupingParked>();
  for (const entry of parked) {
    const held = merged.get(entry.kind);
    if (!held) {
      merged.set(entry.kind, { ...entry, subjects: [...entry.subjects] });
      continue;
    }
    held.devices += entry.devices;
    held.entries += entry.entries;
    for (const subject of entry.subjects) {
      if (!held.subjects.includes(subject)) held.subjects.push(subject);
    }
  }

  return [...merged.values()].map((entry) => ({
    id: `note:${entry.kind}`,
    text: line(t, `karlGroupingParked_${entry.kind}`, {
      count: entry.devices || entry.entries,
    }),
    subjects: entry.subjects,
    devices: entry.devices,
  }));
};

export const buildKarlAsks: BuildKarlAsks = ({
  steps,
  tags,
  added = {},
  removed = {},
  placed = {},
  confirmed = [],
  settled = [],
  removedDevices = [],
  removalGroups = [],
  zoneCountByLabel = {},
  sourceNames = {},
  mainBoard = null,
  feeds,
  heights,
  feeders,
  planBoards,
  t,
}) => {
  const settledMain =
    mainBoard && (mainBoard.status === "answered" || mainBoard.status === "note")
      ? mainBoard.name
      : null;
  const answeredKeys = answeredSourceKeys(settled);
  const roster = buildSourceRoster({
    sources: steps.sources,
    schematicBoards: steps.schematicBoards,
    agent: steps.agent,
    added,
    removed,
    placed,
    confirmed,
    mainBoardName: settledMain,
    planBoards,
  });

  const sources = buildBoardAsks({
    roster,
    main: mainBoard,
    feeds,
    heights,
    feeders,
    confirmedKeys: new Set(confirmed),
    mainBoard: settledMain ?? steps.agent.mainBoard,
    scheduleBoard: steps.agent.scheduleBoard,
    markers: steps.markerPositions,
    zoneCountByLabel,
    names: sourceNames,
    answeredKeys,
    t,
  });

  const work = buildGroupingWork({
    steps,
    tags: tags ?? (steps.tagsOnPlan.length ? { onPlan: steps.tagsOnPlan } : null),
  });

  // Every circuit, walked in order. There is no separate grouping stage: a
  // stray device and a bag grouped by symbol are facts ABOUT a route, and they
  // ride on it. Asked on their own they were questions about a thing the
  // estimator could not see, which is what "even i cannot understand" meant.
  const walk = buildRouteWalk({ steps });
  const status = readRouteStatus({
    settled,
    circuitIds: walk.routes.map((route) => route.circuitId),
  });
  const corrected = new Set(
    Object.keys(status).filter((circuitId) => status[circuitId] === "corrected"),
  );
  const routes = buildRouteAsks({ walk, corrected, t });

  // Behind the walk, and behind the boards: a removal outlives the session that
  // made it, so asked first it opened the panel on a question about yesterday.
  // Last also keeps the tail stable — a removal made mid-walk appends, and the
  // counter grows rather than shuffling under him.
  const removals = buildRemovalAsks({
    removed: removedDevices,
    groups: removalGroups,
    t,
  });

  return {
    // Boards first, in their own group: the circuit walk is locked until they are settled.
    asks: [...sources, ...(heights ? buildHeightAsks({ heights, t }) : []), ...routes, ...removals],
    notes: [...notesOf(work.parked, t), ...emptyWayNote(work.work, t)],
  };
};
